import { readFloatLE, readInt16LE, readInt32LE, readUInt16LE, readUInt8 } from "../buffer-io";
import type { ChannelDefinition } from "./types";

/** IEEE half-precision (float16) stored as uint16 LE — used by AIM decoder types 1 and 20. */
export function decodeFloat16(raw: number): number {
  const sign = (raw & 0x8000) >> 15;
  const exponent = (raw & 0x7c00) >> 10;
  const fraction = raw & 0x03ff;

  if (exponent === 0) {
    if (fraction === 0) {
      return sign ? -0 : 0;
    }
    return (sign ? -1 : 1) * 2 ** -14 * (fraction / 1024);
  }

  if (exponent === 0x1f) {
    return fraction ? Number.NaN : sign ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  }

  return (sign ? -1 : 1) * 2 ** (exponent - 15) * (1 + fraction / 1024);
}

const UNIT_MAP: Record<number, [unit: string, decimals: number]> = {
  1: ["%", 2],
  3: ["g", 2],
  4: ["deg", 1],
  5: ["deg/s", 1],
  6: ["", 0],
  9: ["Hz", 0],
  11: ["", 0],
  12: ["mm", 0],
  14: ["bar", 2],
  15: ["rpm", 0],
  16: ["km/h", 0],
  17: ["C", 1],
  18: ["ms", 0],
  19: ["Nm", 0],
  20: ["km/h", 0],
  21: ["mV", 1],
  22: ["l", 1],
  24: ["l/s", 0],
  26: ["time?", 0],
  27: ["A", 0],
  30: ["lambda", 2],
  31: ["gear", 0],
  33: ["%", 2],
  43: ["kg", 3],
};

export function resolveChannelUnits(unitTypeByte: number): { unit: string; decimals: number } {
  const base = unitTypeByte & 0x7f;
  const [unit, decimals] = UNIT_MAP[base] ?? ["", 0];
  if (unitTypeByte & 0x80 && unit === "mV") {
    return { unit: "V", decimals };
  }
  return { unit, decimals };
}

export function decodeChannelSample(channel: ChannelDefinition, data: Buffer): number {
  if (data.length !== channel.dataSize) {
    throw new Error(
      `Sample size mismatch for ${channel.shortName}: expected ${channel.dataSize}, got ${data.length}`,
    );
  }

  switch (channel.decoderType) {
    case 6:
      return data.length >= 4 ? readFloatLE(data, 0) : Number.NaN;
    case 1:
    case 20:
      return data.length >= 2 ? decodeFloat16(readUInt16LE(data, 0)) : Number.NaN;
    case 4:
    case 11:
      return data.length >= 2 ? readInt16LE(data, 0) : Number.NaN;
    case 13:
      return data.length >= 1 ? readUInt8(data, 0) : Number.NaN;
    case 15:
      return data.length >= 2 ? readUInt16LE(data, 0) : Number.NaN;
    case 0:
    case 3:
    case 8:
    case 12:
    case 22:
    case 24:
    case 26:
    case 27:
    case 31:
    case 32:
    case 33:
    case 37:
    case 38:
    case 39:
      return data.length >= 4 ? readInt32LE(data, 0) : Number.NaN;
    default:
      if (data.length === 4) {
        return readFloatLE(data, 0);
      }
      if (data.length === 2) {
        return readInt16LE(data, 0);
      }
      if (data.length === 1) {
        return readUInt8(data, 0);
      }
      return Number.NaN;
  }
}
